import toast from 'react-hot-toast';
import { logout } from '../store/slices/authSlice';
import {
  BackendError,
  ProcessedError,
  ErrorSeverity,
  ErrorCategory,
  ErrorContext,
  ERROR_CODES,
  ErrorHandlingConfig,
  DEFAULT_ERROR_CONFIG
} from '../types/errors';

export class ErrorHandlingService {
  private static instance: ErrorHandlingService
  private lastShownErrors: Map<string, number> = new Map();
  private errorLog: ProcessedError[] = [];
  private config: ErrorHandlingConfig = DEFAULT_ERROR_CONFIG;
  private onLogout?: () => void;

  static getInstance(): ErrorHandlingService {
    if (!ErrorHandlingService.instance) {
      ErrorHandlingService.instance = new ErrorHandlingService();
    }
    return ErrorHandlingService.instance;
  }

  private constructor() { }

  setConfig(config: Partial<ErrorHandlingConfig>) {
    this.config = { ...this.config, ...config };
  }

  setLogoutCallback(cb: () => void) {
    this.onLogout = cb;
  }

  private generateErrorId(): string {
    return `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private determineSeverity(status: number, code: string): ErrorSeverity {
    if (status === 403 && code === ERROR_CODES.NO_ROLES) {
      return ErrorSeverity.MEDIUM; // User needs role assignment
    }
    if (status >= 500) return ErrorSeverity.HIGH;
    if (status >= 400) return ErrorSeverity.MEDIUM;
    return ErrorSeverity.LOW;
  }

  private determineCategory(status: number, code: string): ErrorCategory {
    if (status === 401) return ErrorCategory.AUTHENTICATION;
    if (status === 403) return ErrorCategory.AUTHORIZATION;
    if (status === 422) return ErrorCategory.VALIDATION;
    if (status >= 500) return ErrorCategory.SERVER;
    if (status === 0) return ErrorCategory.NETWORK;
    if (code === 'TIMEOUT_ERROR') return ErrorCategory.NETWORK;
    return ErrorCategory.UNKNOWN;
  }

  private getUserFriendlyMessage(error: any, status: number): string {
    const code = error.error?.code || error.code || 'UNKNOWN';
    const message = error.error?.message || error.message || 'Unknown error';

    switch (code) {
      case ERROR_CODES.NO_ROLES:
        return "You don't have any roles assigned. Please contact an administrator to assign you appropriate roles.";
      case ERROR_CODES.UNAUTHORIZED:
        return "Your session has expired. Please log in again.";
      case ERROR_CODES.FORBIDDEN:
        return "You don't have permission to perform this action.";
      case ERROR_CODES.VALIDATION_ERROR:
        return "Please check your input and try again.";
      case ERROR_CODES.NETWORK_ERROR:
        return "Network connection issue. Please check your internet connection.";
      case ERROR_CODES.SERVER_ERROR:
        return "Server is experiencing issues. Please try again later.";
      default:
        return message;
    }
  }

  private isRetryable(status: number, code: string): boolean {
    // Don't retry auth errors or validation errors
    if (status === 401 || status === 403 || status === 422) return false;
    // Retry server errors and network issues
    if (status >= 500 || status === 0) return true;
    return false;
  }

  async handleApiError(
    error: any,
    context: string = 'API Operation',
    component?: string,
    userId?: string  // Accept userId as parameter instead of accessing store
  ): Promise<ProcessedError> {
    const timestamp = Date.now();

    let processedError: ProcessedError;

    if (error?.response?.data) {
      // Backend error with structured response
      const backendData = error.response.data as any;
      const status = error.response.status;
      const code = backendData.error?.code || backendData.code || 'UNKNOWN';
      const rawMessage = backendData.error?.message || backendData.message || 'Unknown error';

      processedError = {
        id: this.generateErrorId(),
        message: rawMessage,
        userMessage: this.getUserFriendlyMessage(backendData, status),
        code,
        status,
        severity: this.determineSeverity(status, code),
        category: this.determineCategory(status, code),
        context: { operation: context, component, timestamp, userId },
        timestamp,
        retryable: this.isRetryable(status, code),
        originalError: error,
      };

      // Handle specific error cases
      await this.handleSpecificError(processedError);
    } else if (error?.request) {
      // Network error (no response received) or timeout
      const isTimeout = error.code === 'ECONNABORTED' || error.message?.includes('timeout');
      processedError = {
        id: this.generateErrorId(),
        message: isTimeout ? 'Request timeout - server took too long to respond' : 'Network error - no response received',
        userMessage: isTimeout
          ? 'The request is taking longer than expected. Please try again.'
          : 'Network connection issue. Please check your internet connection.',
        code: isTimeout ? 'TIMEOUT_ERROR' : ERROR_CODES.NETWORK_ERROR,
        status: 0,
        severity: ErrorSeverity.MEDIUM,
        category: ErrorCategory.NETWORK,
        context: { operation: context, component, timestamp, userId },
        timestamp,
        retryable: true,
        originalError: error,
      };
    } else {
      // Generic error
      processedError = {
        id: this.generateErrorId(),
        message: error?.message || 'Unknown error occurred',
        userMessage: 'An unexpected error occurred. Please try again.',
        code: 'UNKNOWN',
        status: 0,
        severity: ErrorSeverity.MEDIUM,
        category: ErrorCategory.UNKNOWN,
        context: { operation: context, component, timestamp, userId },
        timestamp,
        retryable: false,
        originalError: error,
      };
    }

    // Log error
    this.logError(processedError);

    // Show user notification if configured
    if (this.config.showToast) {
      this.showUserNotification(processedError);
    }

    return processedError;
  }

  private async handleSpecificError(error: ProcessedError): Promise<void> {
    switch (error.code) {
      case ERROR_CODES.NO_ROLES:
        await this.handleNoRolesError(error);
        break;
      case ERROR_CODES.UNAUTHORIZED:
        await this.handleUnauthorizedError(error);
        break;
      case ERROR_CODES.FORBIDDEN:
        await this.handleForbiddenError(error);
        break;
      default:
        break;
    }
  }

  private async handleNoRolesError(error: ProcessedError): Promise<void> {
    // This is a special case - user needs role assignment
    // We'll show a special notification and potentially redirect to role assignment
    console.log('User has no roles assigned - needs role assignment flow');

    // You can implement custom logic here, such as:
    // - Redirecting to a role assignment page
    // - Showing a modal for role assignment
    // - Triggering a specific workflow
  }

  private async handleUnauthorizedError(error: ProcessedError): Promise<void> {
    // Auto-logout user if configured
    if (this.config.autoLogout) {
      if (this.onLogout) {
        this.onLogout();
      } else {
        localStorage.removeItem("token");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("user");
        window.location.href = '/login';
      }
    }
  }

  private async handleForbiddenError(error: ProcessedError): Promise<void> {
    // Handle forbidden errors (user doesn't have permission)
    // Could show a permission upgrade modal or redirect to appropriate page
    console.log('User lacks permission for this action');
  }

  private logError(error: ProcessedError): void {
    if (this.config.logToConsole) {
      console.group(`🚨 Error: ${error.code}`);
      console.error('Error Details:', error);
      console.error('Original Error:', error.originalError);
      console.groupEnd();
    }

    // Add to error log
    this.errorLog.push(error);

    // Keep only last 100 errors
    if (this.errorLog.length > 100) {
      this.errorLog = this.errorLog.slice(-100);
    }

    // Log to server if configured
    if (this.config.logToServer) {
      this.logToServer(error).catch(console.error);
    }
  }

  private async logToServer(error: ProcessedError): Promise<void> {
    try {
      // You can implement server-side error logging here
      // For now, we'll just console.log it
      console.log('Logging error to server:', error);
    } catch (logError) {
      console.error('Failed to log error to server:', logError);
    }
  }

  private showUserNotification(error: ProcessedError): void {
    // Check if we've already shown this error recently to prevent duplicates
    const now = Date.now();
    const lastShownKey = `error_${error.code}_${error.message}`;
    const lastShown = this.lastShownErrors.get(lastShownKey);

    if (lastShown && (now - lastShown) < 5000) { // 5 second cooldown
      return; // Don't show duplicate error within 5 seconds
    }

    this.lastShownErrors.set(lastShownKey, now);

    const toastOptions = {
      duration: error.severity === ErrorSeverity.CRITICAL ? 8000 : 5000,
      position: 'top-right' as const,
      style: {
        background: this.getToastColor(error.severity),
        color: '#fff',
        borderRadius: '8px',
        fontSize: '14px',
        maxWidth: '400px',
      },
    };

    // Show different toast types based on severity
    if (error.severity === ErrorSeverity.CRITICAL) {
      toast.error(error.userMessage, toastOptions);
    } else if (error.severity === ErrorSeverity.HIGH) {
      toast.error(error.userMessage, toastOptions);
    } else if (error.severity === ErrorSeverity.MEDIUM) {
      toast.error(error.userMessage, toastOptions);
    } else {
      toast.error(error.userMessage, toastOptions);
    }
  }

  private getToastColor(severity: ErrorSeverity): string {
    switch (severity) {
      case ErrorSeverity.CRITICAL:
        return '#dc2626'; // Red
      case ErrorSeverity.HIGH:
        return '#ea580c'; // Orange
      case ErrorSeverity.MEDIUM:
        return '#d97706'; // Amber
      case ErrorSeverity.LOW:
        return '#059669'; // Green
      default:
        return '#6b7280'; // Gray
    }
  }

  getErrorLog(): ProcessedError[] {
    return [...this.errorLog];
  }

  clearErrorLog(): void {
    this.errorLog = [];
  }

  getErrorsByCode(code: string): ProcessedError[] {
    return this.errorLog.filter(error => error.code === code);
  }

  getErrorsByCategory(category: ErrorCategory): ProcessedError[] {
    return this.errorLog.filter(error => error.category === category);
  }
}

export const errorHandlingService = ErrorHandlingService.getInstance();
export default errorHandlingService; 